import { createFileRoute } from "@tanstack/react-router";

import { faqItems } from "@/lib/content";
import { IMAGES } from "@/lib/images";
import { JsonLd } from "@/components/site/JsonLd";
import { localBusinessSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";
import { FadeUp } from "@/components/site/FadeUp";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { InnerHero } from "@/components/site/PageBits";
import { CtaBand } from "@/components/site/CTA";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ | Pretty in Pink Cleaning Service" },
      {
        name: "description",
        content:
          "Answers to common questions about our residential and commercial cleaning in Wetumpka, AL — services, scheduling, supplies, move-out cleans, and service area.",
      },
      { property: "og:title", content: "FAQ | Pretty in Pink Cleaning Service" },
      {
        property: "og:description",
        content:
          "Answers to common questions about residential and commercial cleaning in Wetumpka, AL.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: IMAGES.coverBanner },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMAGES.coverBanner },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  // Answer-first text for AEO: lead with the short answer.
  const grouped = faqItems.reduce<Record<string, typeof faqItems>>((acc, item) => {
    (acc[item.category] ||= []).push(item);
    return acc;
  }, {});
  const categories = Object.keys(grouped);

  return (
    <>
      <JsonLd data={localBusinessSchema()} />
      <JsonLd data={faqSchema(faqItems.map(({ question, answer }) => ({ question, answer })))} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "FAQ", path: "/faq" },
        ])}
      />
      <InnerHero
        eyebrow="FAQ"
        title="Frequently asked questions"
        subtitle="Quick, clear answers about how our cleaning works — what's included, how to schedule, what to expect, and where we serve."
      >
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "FAQ", path: "/faq" },
            ]}
          />
        </div>
      </InnerHero>

      <section className="px-4 py-12 md:px-6 md:py-16">
        <FadeUp className="mx-auto mb-10 max-w-3xl text-lg text-muted-foreground text-pretty">
          We get a lot of the same questions, so here are direct answers. If something's not
          covered, just reach out — we're happy to help.
        </FadeUp>

        <div className="mx-auto max-w-3xl space-y-12">
          {categories.map((cat) => (
            <div key={cat}>
              <FadeUp>
                <h2 className="text-xl font-bold text-ink md:text-2xl">{cat}</h2>
              </FadeUp>
              <div className="mt-5 divide-y divide-border rounded-2xl border border-border bg-card">
                {(grouped[cat] ?? []).map((q) => (
                  <details key={q.question} className="group p-5 md:p-6">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-ink">
                      {q.question}
                      <span className="text-primary transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <p className="mt-3 text-sm text-muted-foreground md:text-base text-pretty">
                      {q.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CtaBand
        title="Still have questions?"
        subtitle="We're happy to walk you through how it all works. Reach out and we'll get you answers — and a quote."
      />
    </>
  );
}
